import Layout from '@comp/layout'
import Head from 'next/head'
import styles from '@style/ProductPage.module.css'
import heroStyles from '@style/Hero.module.css'
import Image from 'next/image'
import { motion } from 'framer-motion'
import Link from 'next/link'
import CustomerList from '@comp/customers-list'
import { useState, useRef } from 'react'

export default function MonosignMainPage() {
  const nameField = useRef<HTMLInputElement>(null)
  const emailField = useRef<HTMLInputElement>(null)

  enum ResponseType {
    success = 'success',
    failure = 'failure',
    none = 'none',
  }

  const [responseType, setResponseType] = useState<ResponseType>(
    ResponseType.none
  )
  const [responseMessage, setResponseMessage] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const handleSubmit = (e: any) => {
    e.preventDefault()
    setIsSubmitting(true)
    let data = {
      name: nameField.current?.value ?? '',
      email: emailField.current?.value ?? '',
      pdfType: 'monosign',
    }
    fetch('/api/subscribe_pdf', {
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }).then((res) => {
      setIsSubmitting(false)
      // console.log('Response received')
      if (res.status === 200) {
        setResponseType(ResponseType.success)
        setResponseMessage(
          'Thanks for submitting! Our experts will be in touch with you shortly.'
        )
        // console.log('Response succeeded!')
        nameField.current!.value = ''
        emailField.current!.value = ''
      } else {
        setResponseMessage(
          'It seems something has gone wrong. You can directly mail <a href="mailto:sales@monofor.com">sales@monofor.com</a> instead.'
        )
        setResponseType(ResponseType.failure)
      }
    })
  }

  return (
    <Layout miniMode={true}>
      <Head>
        <title>Monofor - Monosign IAM</title>
      </Head>
      <section id={styles.monosignPage}>
        <div className={`${styles.productNavContainer} ${styles.monosign}`}>
          <div className={styles.productNav}>
            <div className={styles.monosignLogo}>
              <Image
                src="/images/product-logos/monosign-logo.svg"
                width={160}
                height={29}
                alt="Monosign Logo"
              />
            </div>
          </div>
        </div>
      </section>
      <section className={heroStyles.heroContainer}>
        <div className={`${heroStyles.heroContent} ${heroStyles.monosignPage}`}>
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ ease: 'easeOut', duration: 0.8 }}
          >
            Identity & Access <br />
            Management (IAM)
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ ease: 'easeOut', duration: 0.8, delay: 0.8 }}
          >
            IAM capabilities including single sign-on, multi-factor <br />
            authentication, automated provisioning and more… <br />
            <br />
            All in one platform.
          </motion.p>
          <motion.div
            className={heroStyles.heroCtaButtons}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ ease: 'easeIn', duration: 0.8, delay: 1.6 }}
          >
            <Link href={`#${styles.downloadBrochure}`}>
              <button className={heroStyles.heroCtaButton}>
                Download our Product Brief
              </button>
            </Link>
            <Link href="/contact">
              <button className={heroStyles.heroCtaOutlineButton}>
                Talk with our experts
              </button>
            </Link>
          </motion.div>
        </div>
        <div className={`${styles.heroImage} ${styles.monosign}`}></div>
      </section>
      <CustomerList
        text="Join customers who secure their identities via Monofor"
        bgColor="#4e6856"
      />
      <section id={styles.downloadBrochure} className={styles.monosignBrochure}>
        <div className={styles.downloadBrochureInner}>
          <div className={styles.downloadBrochureInfo}>
            <div className={styles.downloadBrochureTitle}>
              <h2>Want to learn more?</h2>
              <p>
                Download our Product Brief in PDF format by entering your work
                email.
              </p>
            </div>
            <div
              className={`${heroStyles.brochureFormContainer} ${heroStyles.monosignBrochureFormContainer}`}
            >
              <form className={heroStyles.contactForm} onSubmit={handleSubmit}>
                <fieldset className={heroStyles.inputGroup}>
                  <label htmlFor="name">Name (Required)</label>
                  <input
                    type="text"
                    name="name"
                    required
                    minLength={2}
                    className={heroStyles.inputField}
                    ref={nameField}
                    autoComplete="name"
                    placeholder="Jane Doe"
                  />
                </fieldset>
                <fieldset className={heroStyles.inputGroup}>
                  <label htmlFor="email">Email (Required)</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className={heroStyles.inputField}
                    ref={emailField}
                    autoComplete="email"
                    placeholder="you@your-company.com"
                  />
                </fieldset>
                <div className={heroStyles.formFeedback}>
                  <span
                    className={responseType}
                    dangerouslySetInnerHTML={{ __html: responseMessage }}
                  ></span>
                </div>
                <input
                  type="submit"
                  value={isSubmitting ? 'Sending…' : 'Submit →'}
                  disabled={isSubmitting}
                  className={isSubmitting ? heroStyles.disabled : ''}
                />
              </form>
            </div>
          </div>
          <div className={styles.brochureImage}>
            <Image
              src="/images/monosign-brochure-preview.png"
              width={450}
              height={387}
              alt="Monosign brochure preview"
            />
          </div>
        </div>
      </section>
    </Layout>
  )
}
